import type { IMeshBounds } from "@project/emerge-types";
import { ReferenceGuideType } from "@project/emerge-types";
import {
  Box3,
  type Material,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  SphereGeometry,
  Vector3,
} from "three";
import {
  convertToModernMaterial,
  setMaterialsDoubleSided,
} from "../new/supplyPartMaterials";

export type MeshEditorTransform = {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: number;
};

export interface MeshEditorModelResources {
  object: Object3D;
  /** Source bounds, before editor scale, offsets, rotations or the marker. */
  bounds: IMeshBounds | null;
  update: (
    transform: MeshEditorTransform,
    baseRotationX: number,
    referenceGuideType: ReferenceGuideType,
  ) => void;
  dispose: () => void;
}

function measureMeshBounds(object: Object3D): IMeshBounds | null {
  object.updateWorldMatrix(true, true);
  const box = new Box3().setFromObject(object);
  if (box.isEmpty()) return null;

  const size = box.getSize(new Vector3());
  const center = box.getCenter(new Vector3());
  if (
    ![...size.toArray(), ...center.toArray()].every(Number.isFinite) ||
    size.x <= 0 ||
    size.y <= 0 ||
    size.z <= 0
  ) {
    return null;
  }

  return {
    width: size.x,
    height: size.y,
    depth: size.z,
    offsetX: center.x,
    offsetY: center.y,
    offsetZ: center.z,
  };
}

/**
 * ThreeTools owns the cached geometry/materials/textures, but returns a cloned
 * Object3D hierarchy. Keep that hierarchy intact and own only our replacements
 * and marker. Neither R3F primitives nor imperative children are auto-disposed.
 */
export function createMeshEditorModel(
  loadedObject: Object3D,
  materialOverride?: Material,
): MeshEditorModelResources {
  const object = new Object3D();
  const baseRotationGroup = new Object3D();
  const sunlabsNormalize = new Object3D();
  const originMarkerNode = new Object3D();
  const sourceMaterials = new Map<Mesh, Material | Material[]>();
  const ownedMaterials = new Set<Material>();
  let markerGeometry: SphereGeometry | null = null;
  let disposed = false;

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    object.removeFromParent();
    for (const [mesh, source] of sourceMaterials) mesh.material = source;
    sourceMaterials.clear();
    for (const material of ownedMaterials) material.dispose();
    ownedMaterials.clear();
    markerGeometry?.dispose();
    // Material.dispose does NOT dispose its maps: those stay cache-owned.
  };

  try {
    const bounds = measureMeshBounds(loadedObject);
    const resolveMaterial = (source: Material): Material | Material[] => {
      const converted = materialOverride
        ? source.clone()
        : convertToModernMaterial(source);
      const own = (candidate: Material): Material => {
        // Some converters pass unsupported materials through. Never mutate or
        // dispose that borrowed source, even if conversion returns it verbatim.
        const material = candidate === source ? source.clone() : candidate;
        ownedMaterials.add(material);
        setMaterialsDoubleSided(material);
        return material;
      };
      return Array.isArray(converted) ? converted.map(own) : own(converted);
    };

    loadedObject.traverse((child) => {
      if (!(child instanceof Mesh)) return;
      sourceMaterials.set(child, child.material);
      const source = materialOverride ?? child.material;
      // Resolve one slot at a time so a later failure can release earlier slots.
      child.material = Array.isArray(source)
        ? source.flatMap(resolveMaterial)
        : resolveMaterial(source);
      if (child.geometry && !child.geometry.boundingSphere) {
        child.geometry.computeBoundingSphere();
      }
    });

    markerGeometry = new SphereGeometry(2, 16, 16);
    const markerMaterial = new MeshBasicMaterial({ color: 0x3399ff });
    ownedMaterials.add(markerMaterial);
    originMarkerNode.add(new Mesh(markerGeometry, markerMaterial));
    sunlabsNormalize.add(loadedObject);
    baseRotationGroup.add(sunlabsNormalize, originMarkerNode);
    object.add(baseRotationGroup);

    return {
      object,
      bounds,
      update: (transform, baseRotationX, referenceGuideType) => {
        if (disposed) return;
        const { position, rotation, scale } = transform;
        loadedObject.scale.setScalar(scale);
        sunlabsNormalize.position.set(-position[0], -position[2], position[1]);
        sunlabsNormalize.rotation.set(
          (rotation[0] * Math.PI) / 180,
          (rotation[1] * Math.PI) / 180,
          (rotation[2] * Math.PI) / 180,
        );
        baseRotationGroup.rotation.set(baseRotationX, 0, 0);
        // The marker shares the offset and base rotation, NOT editor rotation.
        originMarkerNode.position.copy(sunlabsNormalize.position);
        const isPlane =
          referenceGuideType === ReferenceGuideType.HorizontalPlane ||
          referenceGuideType === ReferenceGuideType.VerticalPlane;
        object.rotation.set(isPlane ? 0 : Math.PI, 0, 0);
        object.updateWorldMatrix(true, true);
      },
      dispose,
    };
  } catch (error) {
    dispose();
    throw error;
  }
}
